// AdminDashboard.jsx
import React, { useEffect, useState , useMemo } from "react";
import { FaCubes, FaFilePdf, FaUserPlus, FaEdit, FaTrash, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FaCheck, FaXmark } from "react-icons/fa6";
import useIsMobile from '../hooks/useIsMobile';

//import { CheckCircle, XCircle } from "lucide-react";
import LogoPreview from './LogoPreview';
import UserModal from "./UserModal";

const AdminDashboard = ({ onViewModel, onViewPdf, defaultTab = "hospitals" }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [hospitals, setHospitals] = useState([]);
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [lastViewedPatientId, setLastViewedPatientId] = useState(null);
  const [syncing, setSyncing] = useState(false); 
//  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    hospital_id: "",
    enabled: true, // new field
  });
  const [editUser, setEditUser] = useState(null);          // which user is being edited
  const [selectedHospitals, setSelectedHospitals] = useState([]); // hospital IDs for the edit form
  const [newPassword, setNewPassword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [devices, setDevices] = useState([]);
  const [expandedAdminCards, setExpandedAdminCards] = useState({});

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalMode, setUserModalMode] = useState("add");
  const [editingUser, setEditingUser] = useState(null);
  const [isAdminRegistration, setIsAdminRegistration] = useState(false);

  // Detect screen size for responsive design
  const isMobile = useIsMobile();

  const HOSPITALS_URL = window.API_BASE_URL + "hospitals";
  const PATIENTS_URL = window.API_BASE_URL + "patients";
  const USERS_URL = window.API_BASE_URL + "users";
  const SYNC_HOSPITAL_URL = window.API_BASE_URL + "sync-hospital";
  const SYNC_ALL_URL = window.API_BASE_URL + "sync-all";
  const MODEL_URL = window.API_BASE_URL + "model";
  const PDF_URL = window.API_BASE_URL + "pdf";
  const DEVICES_URL = window.API_BASE_URL + "devices";


  // --- Step 1: Helper functions ---
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  // Function to handle selecting a patient row
  const selectPatient = (patient) => {
    const key = `${patient.patient_id}-${patient.hospital_id}`;
    setLastViewedPatientId(key);
    localStorage.setItem("lastViewedPatientId", key);
  };

  // Function to handle hospital logo upload
  const handleLogoUpload = (hospitalId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    fetch(`${window.API_BASE_URL}hospitals/${hospitalId}/logo`, {
      method: "POST",
      body: formData,
    })
      .then((r) => r.json())
      .then(refreshHospitals)
      .catch(console.error);
  };    

  // Load hospitals, users, patients
  useEffect(() => {
    //fetch(`${HOSPITALS_URL}`)
    //  .then((res) => res.json())
    //  .then(setHospitals)
    //  .catch(console.error);

    refreshHospitals();
    
    refreshUsers();
    
    fetch(`${DEVICES_URL}`)
      .then(res => res.json())
      .then(setDevices)
      .catch(console.error);

    fetch(`${PATIENTS_URL}`)
      .then((res) => res.json())
      .then(setPatients)
      .catch(console.error);
      
      const saved = localStorage.getItem("lastViewedPatientId");
      if (saved) setLastViewedPatientId(saved);
      
  }, []);

  const refreshUsers = () => {
    fetch(`${USERS_URL}`)
      .then((res) => res.json())
      .then(setUsers)
      .catch(console.error);
  };
  
  const refreshHospitals = () => {
    fetch(`${HOSPITALS_URL}`)
      .then(res => res.json())
      .then(setHospitals)
      .catch(console.error);
  };  

  const syncHospital = (id) => {
    setSyncing(true);
    fetch(`${SYNC_HOSPITAL_URL}/${id}`, { method: "POST" })
      .then((res) => res.json())
      .then((d) => alert(d.message || "Synced"))
      .catch(console.error)
      .finally(() => setSyncing(false));
  };

  const syncAll = () => {
    setSyncing(true);
    fetch(`${SYNC_ALL_URL}`, { method: "POST" })
      .then((res) => res.json())
      .then((d) => alert(d.message || "All hospitals synced"))
      .catch(console.error)
      .finally(() => setSyncing(false));
  };

  const handleRegisterUser = async (e) => {
    e.preventDefault();

    const payload = {
      username: newUser.username,
      password: newUser.password,
      first_name: newUser.first_name,
      last_name: newUser.last_name,
      hospital_ids: newUser.hospital_id ? [parseInt(newUser.hospital_id)] : [],
      enabled: newUser.enabled ? 1 : 0,
    };

    const res = await fetch(`${USERS_URL}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      //setShowUserModal(false);
      
      setNewUser({
        username: "",
        password: "",
        first_name: "",
        last_name: "",
        hospital_id: "",
        enabled: true,
      });
      refreshUsers();
    }
  };

  const getDateFromTicks = (dotnetTicks) => {
      if (!dotnetTicks || isNaN(dotnetTicks)) return "-";

      const DOTNET_JAVASCRIPT_EPOCH_DIFFERENCE = 621355968000000000;
      const ticksPerMillisecond = 10000;
      const jsMilliseconds = (dotnetTicks - DOTNET_JAVASCRIPT_EPOCH_DIFFERENCE) / ticksPerMillisecond;

      const date = new Date(jsMilliseconds);

      // Return a formatted string, not a Date object
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
        //hour: "2-digit",
        //minute: "2-digit",
      });
   };

   const toggleUserEnabled = async (userId, newStatus) => {
     try {
       const res = await fetch(`${USERS_URL}/${userId}/enabled`, {
         method: "PUT",
         headers: { "Content-Type": "application/json" },
         body: JSON.stringify({ enabled: newStatus ? 1 : 0 }),
       });

       if (res.ok) {
         refreshUsers(); // reload user list
       } else {
         console.error("Failed to update user status");
       }
    } catch (err) {
       console.error("Error toggling user status:", err);
    }
  };

//  const openModal = (user) => {
//    setEditUser(user);
//    setSelectedHospitals(user.hospitals.map(h => h.id));
//    setNewPassword("");
//  };
  
//  const closeModal = () => setEditUser(null);

  // When Add User button is clicked
  const openAddUser = () => {
    setUserModalMode("add");
    setEditingUser({});
    setUserModalOpen(true);
    setIsAdminRegistration(true);
  };

  // When Edit is clicked
  const openEditUser = (user) => {
    setUserModalMode("edit");
    setEditingUser(user);
    setUserModalOpen(true);
    setIsAdminRegistration(false);
  };
  
  // Add these just above handleUserSave
  const createUserAPI = async (data) => {
    try {
      const res = await fetch(USERS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to save user");
      }
    
      refreshUsers();
    } catch (err) {
      console.error("Save user failed:", err.message);
      throw err; // 👈 rethrow so UserModal can catch it
    }      
  };
  
  const updateUserAPI = async (id, data) => {
    try {
      const res = await fetch(`${USERS_URL}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to save user");
      }
      
      refreshUsers();
      
    } catch (err) {
      console.error("Save user failed:", err.message);
      throw err; // 👈 rethrow so UserModal can catch it
    }  
  };
  
  
  // On save
  const handleUserSave = async (data) => {
    try {
      if (userModalMode === "add") {
        await createUserAPI(data);
      } else {
        await updateUserAPI(data.id, data);
      }
      setUserModalOpen(false);
    } catch (err) {
      // ❌ don’t close modal, rethrow to UserModal
      throw err;
    }
  };  
  
  const handleDelete = (id) => {
    if (!window.confirm("Delete this user?")) return;
    fetch(`${USERS_URL}/${id}`, { method: "DELETE" })
      .then(r => r.json())
      .then(() => refreshUsers())
      .catch(console.error);
  };
  
  
//  const saveUserChanges = () => {
//    fetch(`${USERS_URL}/${editUser.id}`, {
//      method: "PUT",
//      headers: { "Content-Type": "application/json" },
//      body: JSON.stringify({
//        first_name: editUser.first_name,
//        last_name:  editUser.last_name,
//        password:   newPassword || null,          // null means keep old password
//        enabled:    editUser.enabled ? 1 : 0,
//        hospital_ids: selectedHospitals
//      }),
//    })
//    .then(r => r.json())
//    .then(() => { closeModal(); refreshUsers(); })
//    .catch(console.error);
//  };

//  const filteredPatients = useMemo(() => {
//    const term = searchTerm.trim().toLowerCase();
//    return patients.filter((p) => {
//      if (!term) return true;
//      return (
//        (p.pid && p.pid.toLowerCase().includes(term)) ||
//        (p.name && p.name.toLowerCase().includes(term)) ||
//        (p.first_name && p.first_name.toLowerCase().includes(term)) ||
//        (p.last_name && p.last_name.toLowerCase().includes(term)) ||
//        (p.hospital_name && p.hospital_name.toLowerCase().includes(term))
//      );
//    });
//  }, [patients, searchTerm]);
  
  const filterBySearchTerm = (list, term, keys) => {
    if (!term) return list;
    const lowerTerm = term.trim().toLowerCase();
    return list.filter(item => keys.some(k => item[k]?.toString().toLowerCase().includes(lowerTerm)));
  };

  // Debounce Hook
  function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const timer = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
  }

  // Usage in AdminDashboard
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  //const filteredPatients = useMemo(() => 
  //  filterBySearchTerm(patients, searchTerm, ["pid", "name", "first_name", "last_name", "hospital_name"]),
  //[patients, searchTerm]);    
  const filteredPatients = useMemo(() => 
    filterBySearchTerm(patients, searchTerm, ["pid", "name", "first_name", "last_name", "hospital_name"]),
  [patients, debouncedSearchTerm]);  
  
  return (
  
      <div className="mx-auto max-w-7xl bg-white p-4 sm:p-6 rounded shadow min-h-screen">
      
  
{syncing && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
      <svg className="animate-spin h-8 w-8 text-blue-600 mb-3" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <p className="text-gray-700">Sync in progress…</p>
    </div>
  </div>
)}       

        <h2 className="text-2xl font-bold text-gray-800 mb-6">Admin</h2>

        {/* Tabs */}
        <div className="flex flex-wrap border-b mb-4 gap-x-4 gap-y-2">
          {["hospitals", "devices", "users", "patients"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium ${
                activeTab === tab
                  ? "border-b-2 border-cyan-900 text-cyan-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* --- Hospitals Tab --- */}
        {activeTab === "hospitals" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-700">Hospitals</h3>
              <button
                onClick={syncAll}
                className="px-4 py-2 bg-cyan-900 text-white rounded hover:bg-cyan-700 text-xs shadow-sm"
              >
                Sync All
              </button>
            </div>
            {/*<div className="rounded-lg shadow relative overflow-visible">
            <div className="rounded-lg overflow-visible"> */}
            {/* Conditionally render table or cards based on screen size */}
            {isMobile ? (
              /* Mobile Card View for Hospitals */
              <div className="space-y-4">
                {hospitals.map((h) => (
                  <div key={h.id}
                       className="bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-cyan-300 p-4 transition-all">
                    
                    {/* Hospital Info Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-800">{h.name}</h4>
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Patients: <span className="font-medium">{h.patient_count}</span></p>
                          <p>Last Synced: <span className="font-medium">{formatDate(h.last_synced)}</span></p>
                        </div>
                      </div>
                      {h.logo_path && (
                        <div className="ml-3">
                          <LogoPreview logoPath={h.logo_path} />
                        </div>
                      )}
                    </div>

                    {/* Action Buttons - Smaller size */}
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => syncHospital(h.id)}
                        className="flex-1 py-2 px-3 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 
                                   transition-colors font-medium text-xs shadow-sm"
                      >
                        Sync Hospital
                      </button>
                      
                      <label className="flex-1 py-2 px-3 bg-green-600 text-white rounded-md hover:bg-green-700 
                                        transition-colors font-medium text-xs shadow-sm cursor-pointer text-center">
                        {h.logo_path ? "Update Logo" : "Upload Logo"}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            handleLogoUpload(h.id, file);
                          }}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                ))}
                
                {hospitals.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No hospitals found.</p>
                  </div>
                )}
              </div>
            ) : (
              /* Desktop Table View for Hospitals */
              <div className="overflow-x-auto w-full rounded-lg shadow">
              <table className="w-full min-w-[600px] border border-gray-300 text-sm sm:text-base text-gray-700 rounded-lg overflow-hidden">
                <thead className="bg-gray-300">
                  <tr>
                    <th className="px-4 py-2 border">Hospital</th>
                    <th className="px-4 py-2 border">Patients</th>
                    <th className="px-4 py-2 border">Last Synced</th>
                    <th className="px-4 py-2 border">Actions</th>
                    <th className="px-4 py-2 border w-24">Logo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {hospitals.map((h) => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border">{h.name}</td>
                      <td className="px-4 py-2 border text-center">{h.patient_count}</td>
                      <td className="px-4 py-2 border text-center">
                        {formatDate(h.last_synced)}
                      </td>
                      <td className="px-4 py-2 border text-center">
                        <button
                          onClick={() => syncHospital(h.id)}
                          className="px-3 py-1 bg-cyan-900 text-white rounded hover:bg-cyan-700 text-xs shadow-sm"
                        >
                          Sync
                        </button>
                      </td>

  <td className="px-2 py-2 border text-center whitespace-nowrap">

    <div className="flex items-center justify-center gap-2 flex-nowrap">

      {/* Upload/Update button */}
      <label className="bg-cyan-700 hover:bg-cyan-800 text-white px-3 py-1 rounded text-xs cursor-pointer">
        {h.logo_path ? "Update" : "Upload"}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (!file) return;
            handleLogoUpload(h.id, file);
          }}
          className="hidden"   // hide native input, label acts as button
        />
      </label>

    {h.logo_path ? (
  <LogoPreview logoPath={h.logo_path} />
    ) : (
      <div
        className="h-8 w-8 flex items-center justify-center bg-gray-100
                   rounded-md text-gray-400 text-xs border"
        title="No logo uploaded"
      />
    )}
         
      
    </div>
  </td>

                      
                    </tr>
                  ))}
                  {hospitals.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-4 py-4 text-center text-gray-500"
                      >
                        No hospitals found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

       {activeTab === "devices" && (
         <div>
           <h3 className="text-lg font-semibold text-gray-700 mb-4">Devices</h3>
           {/* Conditionally render table or cards based on screen size */}
           {isMobile ? (
             /* Mobile Card View for Devices */
             <div className="space-y-4">
               {devices.map((d) => (
                 <div key={d.id}
                      className="bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-blue-300 p-4 transition-all">
                   
                   {/* Device Info */}
                   <div className="mb-3">
                     <h4 className="font-semibold text-lg text-gray-800">{d.tab_name}</h4>
                     <div className="mt-2 text-sm text-gray-600">
                       <p>Hospital: <span className="font-medium">{d.hospital_name}</span></p>
                       <p>Last Synced: <span className="font-medium">{formatDate(d.last_synced)}</span></p>
                     </div>
                   </div>
                 </div>
               ))}
               
               {devices.length === 0 && (
                 <div className="text-center py-8 text-gray-500">
                   <p>No device info found.</p>
                 </div>
               )}
             </div>
           ) : (
             /* Desktop Table View for Devices */
             <div className="overflow-x-auto w-full rounded-lg shadow">
             <table className="w-full min-w-[600px] border border-gray-300 text-sm sm:text-base text-gray-700 rounded-lg overflow-hidden">
               <thead className="bg-gray-300">
                 <tr>
                   <th className="px-4 py-2 border">Hospital</th>
                   <th className="px-4 py-2 border">Tab ID / Name</th>
                   <th className="px-4 py-2 border">Last Synced</th>
                 </tr>
               </thead>
               <tbody>
                 {devices.map((d) => (

                   <tr key={d.id} className="hover:bg-gray-50">
                     <td className="px-4 py-2 border">{d.hospital_name}</td>
                     <td className="px-4 py-2 border text-center">{d.tab_name}</td>
                     <td className="px-4 py-2 border text-center">
                       {formatDate(d.last_synced)}
                     </td>
                  </tr>
                ))}
                {devices.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-4 text-center text-gray-500">
                      No device info found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
           )}
        </div>
      )}
        {activeTab === "users" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Users</h3>
              <button
                onClick={openAddUser}   // 👈 instead of inline setUserModalMode
                title="Register user"
                className="flex items-center gap-2 px-3 py-2 bg-cyan-900 text-white rounded-md shadow text-sm font-semibold hover:bg-cyan-700"
              >
                <FaUserPlus className="text-base" />
                Register User
              </button>
              
              
              
              
            </div>

            {/* Conditionally render table or cards based on screen size */}
            {isMobile ? (
              /* Mobile Card View for Users */
              <div className="space-y-4">
                {users
                  .filter((u) => u.role !== "admin")
                  .map((u) => (
                  <div key={u.id}
                       className="bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-green-300 p-4 transition-all">
                    
                    {/* User Info Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-800">
                          {u.first_name} {u.last_name}
                        </h4>
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Username: <span className="font-medium">{u.username}</span></p>
                          <p>Role: <span className="font-medium capitalize">{u.role}</span></p>
                          <p>Hospital: <span className="font-medium">{(u.hospitals || []).map((h) => h.name).join(", ")}</span></p>
                        </div>
                      </div>
                      <div className="ml-3 flex items-center">
                        {u.enabled ? (
                          <FaCheck className="w-6 h-6 text-green-600" />
                        ) : (
                          <FaXmark className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                    </div>

                    {/* Action Buttons - Smaller size */}
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <button 
                        onClick={() => {
                          setUserModalMode('edit');
                          setEditingUser(u);
                          setUserModalOpen(true);
                        }} 
                        title="Edit user" 
                        className="flex-1 flex items-center justify-center gap-1 py-2 px-2 bg-green-500 
                                   hover:bg-green-600 text-white text-xs rounded-md font-medium shadow-sm transition-colors">
                        <FaEdit className="text-xs" />
                        <span>Edit</span>
                      </button>
                      <button 
                        onClick={() => handleDelete(u.id)} 
                        title="Delete user" 
                        className="flex-1 flex items-center justify-center gap-1 py-2 px-2 bg-red-500 
                                   hover:bg-red-600 text-white text-xs rounded-md font-medium shadow-sm transition-colors">
                        <FaTrash className="text-xs" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
                   
                {users.length <= 1 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No users found.</p>
                  </div>
                )}                     
              </div>
            ) : (
              /* Desktop Table View for Users */
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full border border-gray-300 text-sm sm:text-base text-gray-700 rounded-lg overflow-hidden">
                  <thead className="bg-gray-300">
                    <tr>
                      <th className="px-4 py-2 border">Name</th>
                      <th className="px-4 py-2 border">Username</th>
                      <th className="px-4 py-2 border">Role</th>
                      <th className="px-4 py-2 border">Hospital</th>
                      <th className="px-4 py-2 border">Enabled</th>
                      <th className="px-4 py-2 border">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter((u) => u.role !== "admin")
                      .map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border">
                          {u.first_name} {u.last_name}
                        </td>
                        <td className="px-4 py-2 border">{u.username}</td>
                        <td className="px-4 py-2 border">{u.role}</td>
                        <td className="px-4 py-2 border">
                          {(u.hospitals || []).map((h) => h.name).join(", ")}
                        </td>
  <td className="px-4 py-2 border text-center">
    {u.enabled ? (
      <FaCheck className="w-5 h-5 text-green-600 inline" />
    ) : (
      <FaXmark className="w-5 h-5 text-red-600 inline" />
    )}
  </td>


                        <td className="px-4 py-2 border text-center">
                          <div className="flex items-center justify-center gap-4">
                            <button onClick={() => {
                                      setUserModalMode('edit');
                                      setEditingUser(u);
                                      setUserModalOpen(true);
                                    }} 
                                    title="Edit user" 
                                    className="flex items-center gap-1 px-3 py-1 sm:px-4 sm:py-1.5 bg-green-500 hover:bg-green-700 text-white text-xs rounded-full font-small shadow-sm">
                              <FaEdit className="text-sm" />
                              Edit
                            </button>
                            <button onClick={() => handleDelete(u.id)} title="Delete user" 
                                    className="flex items-center gap-1 px-3 py-1 sm:px-4 sm:py-1.5 bg-red-500 hover:bg-red-700 text-white text-xs rounded-full font-small shadow-sm">
                              <FaTrash className="text-sm" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                       
                    {users.length <= 1 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-4 text-center text-gray-500">
                          No users found.
                        </td>
                      </tr>
                    )}                     
                       
                  </tbody>
                </table>
              </div>
            )}

            {/* Modal */}
            <UserModal
              isOpen={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              onSave={handleUserSave}
              mode={userModalMode}
              initialData={editingUser}
              hospitals={hospitals}
              showHospitalSelect={true}
              isAdminRegistration={isAdminRegistration}  // 👈 pass down
            />
          </div>   
        )}        
        {/* --- Hospitals and Patients remain unchanged ... */}


{/* --- Patients Tab --- */}
{activeTab === "patients" && (
  <div>
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
    <h3 className="text-lg font-semibold text-gray-700">
      Patients <span className="text-gray-500 text-lg">({filteredPatients.length})</span>
    </h3>

    {/* 🔍 Search Bar */}
    {/*<div className="mb-4 flex justify-end sm:justify-end">*/}
      <input
        type="text"
        placeholder="Search patients..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="border border-gray-300 rounded-md px-3 py-1 text-sm w-full sm:w-64
                   focus:outline-none focus:ring-1 focus:ring-cyan-600 focus:border-cyan-600"
      />
    </div>

    {/* Conditionally render table or cards based on screen size */}
    {isMobile ? (
      /* Mobile Compact Card View */
      <div className="flex flex-col gap-2">
        {filteredPatients.map((p) => (
          <div key={`${p.patient_id}-${p.hospital_id}`} 
               className={`bg-white shadow-sm rounded-lg border transition-all ${
                 lastViewedPatientId === `${p.patient_id}-${p.hospital_id}` 
                   ? "border-yellow-400 bg-yellow-50" 
                   : "border-gray-200 hover:border-blue-300"
               }`}>
            
            {/* Main Card Header - Always Visible */}
            <div className="flex items-center justify-between p-3">
              <div className="flex-1" onClick={() => selectPatient(p)}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-sm text-gray-800 leading-tight">
                      {p.first_name || p.last_name 
                        ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                        : p.name}
                    </h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                      ID: {p.pid || "-"}
                    </p>
                  </div>
                  
                  {/* Action Buttons - Always Visible */}
                  <div className="flex gap-2 ml-3">
                    {p.model_drive_id ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          selectPatient(p);
                          onViewModel({
                            patientId: p.patient_id,
                            patientName: p.first_name || p.last_name 
                              ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                              : p.name,
                            modelUrl: `${MODEL_URL}/${p.model_drive_id}`,
                            studyDate: getDateFromTicks(p.dor),
                          });
                        }}
                        className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white 
                                   rounded hover:bg-blue-600 transition-colors"
                        title="View 3D Model">
                        <FaCubes className="text-xs" />
                      </button>
                    ) : (
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-400 rounded">
                        <FaCubes className="text-xs" />
                      </div>
                    )}
                  
                    {p.pdf_drive_id ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          selectPatient(p);
                          onViewPdf({
                            patientId: p.patient_id,
                            patientName: p.name || "",
                            fileId: p.pdf_drive_id,
                            pdfUrl: `${PDF_URL}/${p.pdf_drive_id}`,
                          });
                        }}
                        className="flex items-center justify-center w-8 h-8 bg-red-500 text-white 
                                   rounded hover:bg-red-600 transition-colors"
                        title="View PDF Report">
                        <FaFilePdf className="text-xs" />
                      </button>
                    ) : (
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-400 rounded">
                        <FaFilePdf className="text-xs" />
                      </div>
                    )}
                    
                    {/* Dropdown Toggle */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedAdminCards(prev => ({
                          ...prev,
                          [`${p.patient_id}-${p.hospital_id}`]: !prev[`${p.patient_id}-${p.hospital_id}`]
                        }));
                      }}
                      className="flex items-center justify-center w-8 h-8 bg-gray-200 text-gray-600 
                                 rounded hover:bg-gray-300 transition-colors"
                      title="View Details">
                      {expandedAdminCards[`${p.patient_id}-${p.hospital_id}`] ? 
                        <FaChevronUp className="text-xs" /> : 
                        <FaChevronDown className="text-xs" />
                      }
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Expandable Details - Hidden by Default */}
            {expandedAdminCards[`${p.patient_id}-${p.hospital_id}`] && (
              <div className="px-3 pb-3 border-t border-gray-100">
                <div className="pt-2 space-y-1">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-700">DOB:</span> {getDateFromTicks(p.dob) || "-"}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-700">Registered:</span> {getDateFromTicks(p.dor) || "-"}
                    </p>
                    {p.hospital_name && (
                      <p className="text-gray-600 col-span-2">
                        <span className="font-medium text-gray-700">Hospital:</span> {p.hospital_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        
        {filteredPatients.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No patients found.</p>
          </div>
        )}
      </div>
    ) : (
      /* Desktop Table View */
      <div className="overflow-x-auto rounded-lg shadow">
        <table className="min-w-full border border-gray-300 text-sm sm:text-base text-gray-700 rounded-lg overflow-hidden">
          <thead className="bg-gray-300">
            <tr>
              <th className="px-4 py-2 border">Patient ID</th>
              <th className="px-4 py-2 border">Name</th>
              <th className="px-4 py-2 border">Hospital</th>
              <th className="px-4 py-2 border">Registered Date</th>
              <th className="px-4 py-2 border">3D Model</th>
              <th className="px-4 py-2 border">PDF Report</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredPatients.map((p) => (
              <tr key={`${p.patient_id}-${p.hospital_id}`} 
                  onClick={() => selectPatient(p)}            
                  className={`hover:bg-gray-50 transition cursor-pointer relative ${
                               lastViewedPatientId === `${p.patient_id}-${p.hospital_id}` ? "bg-yellow-100 font-semibold" : ""}`}>
                
                <td className="relative px-4 py-2 border">
                  {lastViewedPatientId === `${p.patient_id}-${p.hospital_id}` && (
                    <div className="absolute left-0 top-0 h-full w-1 bg-yellow-500"></div>
                  )}
                  {p.pid}
                </td>
                
                <td className="px-4 py-2 border">
                  {p.first_name || p.last_name
                    ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                    : p.name}
                </td>

                <td className="px-4 py-2 border">{p.hospital_name}</td>
                <td className="px-4 py-2 border text-center">{getDateFromTicks(p.dor)}</td>

                {/* 3D Model */}
                <td className="px-4 py-2 border text-center">
                  {p.model_drive_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectPatient(p);
                        onViewModel({
                          patientId: p.patient_id,
                          patientName:
                            p.first_name || p.last_name
                              ? `${p.first_name} ${p.last_name}`
                              : p.name,
                          modelUrl: `${MODEL_URL}/${p.model_drive_id}`,
                          studyDate: getDateFromTicks(p.dor),
                        });
                      }}
                      title="View 3D Uterus Model"
                      className="flex items-center gap-1 w-25 px-3 py-1 sm:px-4 sm:py-1.5 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 text-xs font-medium shadow-sm mx-auto"
                    >
                      <FaCubes className="text-sm" /> Model
                    </button>
                  ) : (
                    <span className="flex items-center w-25 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs font-medium shadow-sm mx-auto text-gray-400">No Model</span>
                  )}
                </td>

                {/* PDF Report */}
                <td className="px-4 py-2 border text-center">
                  {p.pdf_drive_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectPatient(p);
                        onViewPdf({
                          patientId: p.patient_id,
                          patientName: p.name || "",
                          fileId: p.pdf_drive_id,
                          pdfUrl: `${PDF_URL}/${p.pdf_drive_id}`,
                        });
                      }}
                      title="View PDF Report"
                      className="flex items-center gap-1 w-20 px-3 py-1 sm:px-4 sm:py-1.5 bg-red-100 text-red-700 rounded-full hover:bg-red-200 text-xs font-medium shadow-sm mx-auto"
                    >
                      <FaFilePdf className="text-sm" /> PDF
                    </button>
                  ) : (
                    <span className="flex items-center w-20 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs font-medium shadow-sm mx-auto text-gray-400">No PDF</span>
                  )}
                </td>
              </tr>
            ))}
            {filteredPatients.length === 0 && (
              <tr>
                <td
                  colSpan="7"
                  className="px-4 py-4 text-center text-gray-500"
                >
                  No patients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

      </div>
  );
};

export default AdminDashboard;
